// src/components/common/PaymentPagination.js
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PaymentPagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  disabled = false
}) => {
  // Calculate the range of items being shown
  const startItem = ((currentPage - 1) * itemsPerPage) + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    
    // Always show first page
    pageNumbers.push(1);
    
    // Calculate range around current page
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);
    
    // Add ellipsis after first page if needed
    if (startPage > 2) {
      pageNumbers.push('...');
    }
    
    // Add pages around current page
    for (let i = startPage; i <= endPage; i++) {
      if (i > 1 && i < totalPages) {
        pageNumbers.push(i);
      }
    }
    
    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      pageNumbers.push('...');
    }
    
    // Always show last page if there's more than one page
    if (totalPages > 1) {
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
      {/* Mobile pagination */}
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || disabled}
          className={`px-4 py-2 border rounded-md ${
            currentPage === 1 || disabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Previous
        </button>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || disabled}
          className={`px-4 py-2 border rounded-md ${
            currentPage === totalPages || disabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          Next
        </button>
      </div>

      {/* Desktop pagination */}
      <div className="hidden sm:flex sm:items-center sm:justify-between w-full">
        {/* Items info */}
        <div className="text-sm text-gray-700">
          Showing <span className="font-semibold">{startItem}</span> to{' '}
          <span className="font-semibold">{endItem}</span> of{' '}
          <span className="font-semibold">{totalItems}</span> payments
        </div>

        {/* Page navigation */}
        <nav className="inline-flex rounded-md shadow-sm -space-x-px">
          {/* Previous button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || disabled}
            className={`px-2 py-2 border rounded-l-md ${
              currentPage === 1 || disabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
            }`}
            title="Previous"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Page numbers */}
          {getPageNumbers().map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-4 py-2 border border-gray-300 bg-white text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  onClick={() => typeof page === 'number' && onPageChange(page)}
                  disabled={disabled}
                  className={`px-4 py-2 border ${
                    currentPage === page
                      ? 'bg-blue-50 text-blue-600 border-blue-300'
                      : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}

          {/* Next button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || disabled}
            className={`px-2 py-2 border rounded-r-md ${
              currentPage === totalPages || disabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
            }`}
            title="Next"
          >
            <ChevronRight size={18} />
          </button>
        </nav>
      </div>
    </div>
  );
};

export default PaymentPagination;